<template lang="pug">
  #app(:class="{'disableScroll': lockScroll}")
    #versionChecker {{version}}
    transition-group(name="fade", mode="out-in")
      router-view(:source="orderedSource", :hasLoaded="hasLoaded", :lastUpdate="lastUpdate", key="content")
</template>

<script>
import pkg from '../package'
import axios from 'axios'
import moment from 'moment'
import { cloneDeep, isEmpty, template, templateSettings } from 'lodash'
import config from './assets/config'

export default {
  name: 'App',
  data () {
    return {
      version: pkg.version,
      source: {},
      lastUpdate: '',
      fetchTimes: 0,
      lockScroll: false,
      period: 10000
    }
  },
  mounted () {
    // 取資料邏輯
    this.startFetch()
  },
  methods: {
    async startFetch () {
      await this.fetchPeriodically()
      this.fetchTimes++
      setTimeout(this.startFetch, this.period)
    },
    isFirstLoad () {
      return this.fetchTimes < 1
    },
    async fetchPeriodically () {
      const task = await this.fetchActivity([7004])
      this.makeActivityInfo(task)

      await this.currentLeaderBoard(task)
      await this.historyLeaderBoard(task)
    },
    makeActivityInfo (task) {
      task.taskIds.forEach((taskId, index) => {
        if (isEmpty(this.source[taskId])) {
          const configKey = task.configKeys[taskId]
          const rankTab = cloneDeep(config[configKey].rankTab)
          rankTab.value = taskId

          const eventAPI = cloneDeep(config[configKey].eventAPI)
          eventAPI.taskId = taskId

          this.$set(this.source, taskId, {
            name: task.taskNames[index],
            tag: task.taskTags[index],
            taskId: taskId,
            rankTab: rankTab,
            eventAPI: eventAPI
          })
        } else {
          this.source[taskId]['name'] = task.taskNames[index]
          this.source[taskId]['tag'] = task.taskTags[index]
          this.source[taskId]['taskId'] = taskId
        }
      })
    },
    makeDayIdList (cycles) {
      this.dataUpsert(this.source[cycles.taskSeqNo], 'dayIdList', cycles.dateRange)
      this.dataUpsert(this.source[cycles.taskSeqNo], 'today', cycles.today)
      this.dataUpsert(this.source[cycles.taskSeqNo], 'historyLeaderBoard', {})
      this.dataUpsert(this.source[cycles.taskSeqNo], 'fetchCycles', Array.from(cycles.data))
    },
    dataUpsert (data, key, value) {
      if (!data[key]) {
        this.$set(data, key, value)
      } else {
        if (typeof value === 'string') {
          data[key] = value
        } else {
          Object.keys(value).forEach(targetKey => {
            data[key][targetKey] = value[targetKey]
          })
        }
      }
    },
    async currentLeaderBoard (task) {
      // 即時榜單
      const leaderBoards = await this.fetchCurrentLeaderBoards(task.taskIds)
      this.lastUpdate = moment().format('YYYY/MM/DD HH:mm:ss')
      leaderBoards.forEach(val => {
        const info = val.info
        info.dateRange = this.dateRangeFormat((info.cycleInfo || {}).report_date_start, (info.cycleInfo || {}).report_date_end)

        this.$set(this.source[val.taskSeqNo], 'currentLeaderBoard', val.data)
        this.$set(this.source[val.taskSeqNo], 'info', info)
      })
    },
    async historyLeaderBoard (task) {
      // 歷史日期
      if (this.isFirstLoad()) {
        for (const taskId of task.taskIds) {
          const cycles = await this.fetchCycle(taskId)
          this.makeDayIdList(cycles)
          // 歷史榜單
          this.processHistory(cycles)
        }
      }
    },
    async processHistory (cycle) {
      const taskId = cycle.taskSeqNo
      for (let circle of cycle.data) {
        const leaderBoards = await this.fetchHistoryLeaderBoards(taskId, circle)
        leaderBoards.forEach(val => {
          this.$set(this.source[val.taskSeqNo]['historyLeaderBoard'], circle, val.data)
          const fetchCycles = this.source[val.taskSeqNo]['fetchCycles']
          fetchCycles.splice(fetchCycles.indexOf(cycle), 1)
        })
      }
    },
    async fetchActivity (gameids) {
      const taskIds = []
      const configKeys = {}
      for (const gameid of gameids) {
        const primary = await axios.post(process.env.API_URL, {
          dao: 'GetActivity',
          gameId: gameid
        })

        const result = primary.data.data.results[0]
        const primaryConfigKey = `job_${gameid}`

        taskIds.push(result.taskId)
        configKeys[result.taskId] = primaryConfigKey

        const binds = Object.keys(result).filter(prop => prop.startsWith('bind_jobid_'))
        for (const bind of binds) {
          if (result[bind]) {
            taskIds.push(result[bind])
            configKeys[result[bind]] = `${primaryConfigKey}_${bind}`
          }
        }
      }
      return { taskIds, taskNames: [], taskTags: [], configKeys }
    },
    async fetchCurrentLeaderBoards (taskIds) {
      const requests = taskIds.map(taskId => {
        const url = this.parseRestUrl(this.findEventAPI(taskId).now, {
          taskId
        })
        return this.buildHttpGetRequest(url)
      })
      return this.parseRankData(await Promise.all(requests))
    },
    async fetchHistoryLeaderBoards (taskId, cycle) {
      const url = this.parseRestUrl(this.findEventAPI(taskId).history, {
        taskId, cycle
      })
      const request = this.buildHttpGetRequest(url)
      return this.parseRankData(Array.of(await request))
    },
    parseRestUrl (url, pathVariables) {
      templateSettings.interpolate = /{([\s\S]+?)}/g
      const complied = template(url)
      return complied(pathVariables)
    },
    buildHttpGetRequest (restUrl) {
      const url = new URL(restUrl, process.env.API_URL)
      const searchParams = url.searchParams
      if (this.playerId) {
        searchParams.append('player', this.playerId)
      }
      return axios.get(url.toString())
    },
    parseRankData (results) {
      return results.map(res => {
        const { activity_info: activityInfo, activity_data: activityData } = res.data.data
        return {
          taskSeqNo: activityInfo.id,
          data: activityData.map((data) => {
            delete Object.assign(data, { id: data['rule_id'] })['rule_id']
            delete Object.assign(data, { name: data['rule_name'] })['rule_name']
            delete Object.assign(data, { len: data['rule_length'] })['rule_length']

            if (data.rank_list) {
              data.rank_list = this.combineScore(data.rank_list)
            }
            return data
          }),
          info: activityInfo
        }
      })
    },
    async fetchCycle (taskId) {
      const url = this.parseRestUrl(this.findEventAPI(taskId).cycles, {
        taskId
      })
      const request = this.buildHttpGetRequest(url)
      const res = await request
      const cycles = res.data.data || []
      return {
        taskSeqNo: taskId,
        data: cycles.map(row => row.cycle),
        dateRange: cycles.map(row => this.dateRangeFormat(row.report_date_start, row.report_date_end))
      }
    },
    combineScore (item) {
      return item.map(({ ...rest }) => {
        if (rest.score === undefined) {
          return rest
        }
        rest.score = { score: rest.score }
        return rest
      })
    },
    dateRangeFormat (sdate, edate) {
      if (sdate === edate) {
        return sdate
      }
      return `${sdate}~${edate}`
    },
    findEventAPI (taskId) {
      return this.source[taskId].eventAPI
    }
  },
  computed: {
    orderedSource () {
      let sourceArray = []
      for (let job in this.source) {
        sourceArray.push(this.source[job])
      }
      return sourceArray
    },
    playerId () {
      return this.$route.query.playerId
    },
    hasLoaded () {
      return this.source && this.fetchTimes >= 1
    }
  }
}
</script>

<style lang="scss">
  @import '~breakpoint-sass/stylesheets/_breakpoint';
  @import 'assets/styles/main';
  //inner scroll style
  html {
    height: 100vh;
    background-color: $black;
    background: url('assets/images/bg.jpg') repeat left top;

    body {
      padding: env(safe-area-inset, 0px);
      height: 100%;

      #app {
        position: relative;
        height: 100%;

        .events {
          // min-height: 150%;
        }
      }
    }
  }

  #app {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  #versionChecker {
    display: none;
  }

  .isFinished {
    position: fixed;
    background-color: $black;
    color: $white;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.4em;
  }
</style>



// WEBPACK FOOTER //
// src/App.vue